import React from 'react'
import { messagingPatternDetection } from '../services/messagingPatternDetection'

interface RateLimitWarningProps {
  userId: string
  onDismiss?: () => void
}

export default function RateLimitWarning({ userId, onDismiss }: RateLimitWarningProps) {
  const rateLimitInfo = messagingPatternDetection.getRateLimitInfo(userId)

  if (!rateLimitInfo.isLimited) {
    return null
  }

  const timeRemaining = rateLimitInfo.expiresAt ? rateLimitInfo.expiresAt - Date.now() : 0
  const hoursRemaining = Math.ceil(timeRemaining / (1000 * 60 * 60))
  const minutesRemaining = Math.ceil(timeRemaining / (1000 * 60))

  const formatTimeRemaining = () => {
    if (timeRemaining <= 0) return 'Soon'
    if (hoursRemaining >= 1) return `${hoursRemaining} hour${hoursRemaining > 1 ? 's' : ''}`
    return `${minutesRemaining} minute${minutesRemaining > 1 ? 's' : ''}`
  }

  const getWarningType = () => {
    if (rateLimitInfo.reason?.includes('vendor')) return 'vendor-limit'
    if (rateLimitInfo.reason?.includes('rapid')) return 'rapid-limit'
    if (rateLimitInfo.reason?.includes('flooding')) return 'flood-limit'
    return 'general-limit'
  }

  const getWarningIcon = () => {
    switch (getWarningType()) {
      case 'vendor-limit': return '🛡️'
      case 'rapid-limit': return '⚡'
      case 'flood-limit': return '🌊'
      default: return '⚠️'
    }
  }

  const getWarningTitle = () => {
    switch (getWarningType()) {
      case 'vendor-limit': return 'Vendor Contact Limit Reached'
      case 'rapid-limit': return 'Messaging Too Fast'
      case 'flood-limit': return 'Message Limit Exceeded'
      default: return 'Rate Limited'
    }
  }

  const getWarningMessage = () => {
    switch (getWarningType()) {
      case 'vendor-limit':
        return 'New users can contact up to 3 vendors per day. This helps protect vendors from spam and ensures quality connections.'
      case 'rapid-limit':
        return 'You\'re sending messages too quickly. Please slow down to ensure meaningful conversations.'
      case 'flood-limit':
        return 'You\'ve sent too many messages in a short time. Take a break and try again later.'
      default:
        return 'Your messaging has been temporarily limited due to suspicious activity patterns.'
    }
  }

  const getActionSuggestions = () => {
    switch (getWarningType()) {
      case 'vendor-limit':
        return [
          'Wait 24 hours to contact more vendors',
          'Focus on meaningful conversations with vendors you\'ve already contacted',
          'Consider upgrading to a trusted member status for higher limits',
          'Contact support if you believe this is an error'
        ]
      case 'rapid-limit':
        return [
          'Wait a few minutes between messages',
          'Take time to craft thoughtful responses',
          'Consider using voice calls for urgent discussions',
          'Messaging will be restored automatically soon'
        ]
      case 'flood-limit':
        return [
          'Take a break from messaging',
          'Review the community guidelines',
          'Consider if your messages are adding value',
          'Limit will reset in about an hour'
        ]
      default:
        return [
          'Review your recent messaging activity',
          'Ensure you\'re following community guidelines',
          'Contact support if you need assistance',
          'Limits will be lifted automatically'
        ]
    }
  }

  return (
    <div className="rate-limit-warning">
      <div className="warning-content">
        <div className="warning-header">
          <div className="warning-icon">{getWarningIcon()}</div>
          <div className="warning-title">
            <h3>{getWarningTitle()}</h3>
            <p className="limit-reset">Resets in: {formatTimeRemaining()}</p>
          </div>
          {onDismiss && (
            <button onClick={onDismiss} className="dismiss-btn" title="Dismiss">
              ×
            </button>
          )}
        </div>

        <div className="warning-message">
          <p>{getWarningMessage()}</p>
        </div>

        <div className="warning-details">
          <div className="details-header">
            <h4>What you can do:</h4>
          </div>
          <ul className="action-suggestions">
            {getActionSuggestions().map((suggestion, index) => (
              <li key={index}>{suggestion}</li>
            ))}
          </ul>
        </div>

        {getWarningType() === 'vendor-limit' && (
          <div className="vendor-protection-info">
            <div className="info-box">
              <h4>🔒 Why this limit exists:</h4>
              <p>
                This protects vendors from spam and helps maintain the quality of our community. 
                Verified vendors receive many messages daily, and these limits ensure they can 
                focus on legitimate business inquiries.
              </p>
            </div>
          </div>
        )}

        <div className="warning-footer">
          <div className="support-links">
            <a href="/community-guidelines" className="support-link">
              📋 Community Guidelines
            </a>
            <a href="/contact-support" className="support-link">
              💬 Contact Support
            </a>
            {getWarningType() === 'vendor-limit' && (
              <a href="/membership-upgrade" className="support-link">
                ⭐ Upgrade Membership
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Hook for checking if user is rate limited
export function useRateLimit(userId: string) {
  const rateLimitInfo = messagingPatternDetection.getRateLimitInfo(userId)
  return {
    isLimited: rateLimitInfo.isLimited,
    reason: rateLimitInfo.reason,
    expiresAt: rateLimitInfo.expiresAt,
    timeRemaining: rateLimitInfo.expiresAt ? rateLimitInfo.expiresAt - Date.now() : 0
  }
}